// Cycle Count - Review and Finalise
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  TrendingUp,
  TrendingDown,
  FileText,
  Download,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

interface CountItem {
  code: string;
  description: string;
  category: string;
  abcClass: string;
  location: string;
  systemQty: number;
  unit: string;
  value: number;
  lastCycleCount: string;
  countedQty: string;
  countedBy: string;
  notes: string;
  isCounted: boolean;
}

interface CycleCountReviewPageProps {
  setupData?: {
    countType: "abc-based" | "manual";
    formData: {
      date: Date;
      warehouse: string;
      assignedTo: string;
      description: string;
      notes: string;
      abcClass?: string;
    };
  };
  countedItems?: CountItem[];
}

export function StockTakesCycleCountReviewPage({ setupData, countedItems = [] }: CycleCountReviewPageProps) {
  const { navigateTo } = useNavigation();
  const [finalNotes, setFinalNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const counted = countedItems.filter(item => item.isCounted);
    const totalItems = countedItems.length;
    const countedItems_count = counted.length;
    
    const variances = counted.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return !isNaN(countedQty) && countedQty !== item.systemQty;
    });
    
    const increases = variances.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return countedQty > item.systemQty;
    });
    
    const decreases = variances.filter(item => {
      const countedQty = parseFloat(item.countedQty);
      return countedQty < item.systemQty;
    });
    
    const totalVarianceQty = variances.reduce((sum, item) => {
      const countedQty = parseFloat(item.countedQty);
      return sum + Math.abs(countedQty - item.systemQty);
    }, 0);
    
    const totalVarianceValue = variances.reduce((sum, item) => {
      const countedQty = parseFloat(item.countedQty);
      const variance = countedQty - item.systemQty;
      return sum + Math.abs(variance * item.value);
    }, 0);

    return {
      totalItems,
      countedItems: countedItems_count,
      variances: variances.length,
      increases: increases.length,
      decreases: decreases.length,
      totalVarianceQty,
      totalVarianceValue,
    };
  }, [countedItems]);

  const handleFinalize = async () => {
    setIsSubmitting(true);
    
    try {
      // Prepare stock take data
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference("Cycle Count");
      
      const stockTakeData: SavedStockTake = {
        id: stockTakeId,
        reference,
        type: "Cycle Count",
        status: "Completed",
        date: setupData?.formData.date || new Date(),
        warehouse: setupData?.formData.warehouse || "",
        assignedTo: setupData?.formData.assignedTo || "",
        description: setupData?.formData.description || "",
        notes: finalNotes || setupData?.formData.notes || "",
        countType: setupData?.countType,
        abcClass: setupData?.formData.abcClass,
        items: countedItems.map(item => ({
          code: item.code,
          description: item.description,
          category: item.category,
          abcClass: item.abcClass,
          location: item.location,
          systemQty: item.systemQty,
          countedQty: parseFloat(item.countedQty) || 0,
          variance: (parseFloat(item.countedQty) || 0) - item.systemQty,
          unit: item.unit,
          value: item.value,
          notes: item.notes,
          countedBy: item.countedBy,
        })),
        statistics: {
          totalItems: stats.totalItems,
          countedItems: stats.countedItems,
          variances: stats.variances,
          increases: stats.increases,
          decreases: stats.decreases,
          totalVarianceValue: stats.totalVarianceValue,
        },
        createdAt: new Date(),
        completedAt: new Date(),
      };

      // Save to storage
      saveStockTake(stockTakeData);

      toast.success("Cycle count finalised successfully", {
        description: `Reference: ${reference}`,
      });

      // Navigate back to stock takes list
      setTimeout(() => {
        navigateTo("stock-takes");
      }, 1000);
    } catch (error) {
      console.error("Error finalising cycle count:", error);
      toast.error("Failed to finalise cycle count");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportReport = () => {
    toast.success("Report exported successfully");
  };

  const getVarianceColor = (systemQty: number, countedQty: string) => {
    const counted = parseFloat(countedQty) || 0;
    const variance = counted - systemQty;
    if (variance === 0) return "text-green-600 dark:text-green-400";
    if (variance > 0) return "text-blue-600 dark:text-blue-400";
    return "text-red-600 dark:text-red-400";
  };

  const getVarianceText = (systemQty: number, countedQty: string) => {
    const counted = parseFloat(countedQty) || 0;
    const variance = counted - systemQty;
    if (variance === 0) return "Match";
    if (variance > 0) return `+${variance}`;
    return `${variance}`;
  };

  const getVarianceValueText = (systemQty: number, countedQty: string, value: number) => {
    const counted = parseFloat(countedQty) || 0;
    const variance = counted - systemQty;
    const varianceValue = variance * value;
    if (varianceValue === 0) return "£0.00";
    if (varianceValue > 0) return `+£${varianceValue.toFixed(2)}`;
    return `-£${Math.abs(varianceValue).toFixed(2)}`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 border-b bg-background">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("stock-takes/cycle-count/count", { setupData })}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Count
          </Button>
        </div>

        {/* Title Section */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
            <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Review Cycle Count</h1>
            <p className="text-muted-foreground">
              {setupData?.countType === "abc-based" 
                ? `ABC Class ${setupData.formData.abcClass} Cycle Count`
                : "Manual Cycle Count"}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Count Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Count Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Items</span>
                <span className="text-sm font-medium">{stats.totalItems}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Counted</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  {stats.countedItems}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Variances</span>
                <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                  {stats.variances}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variance Breakdown */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Variance Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-muted-foreground">Increases</span>
                </div>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {stats.increases}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <span className="text-sm text-muted-foreground">Decreases</span>
                </div>
                <span className="text-sm font-medium text-red-600 dark:text-red-400">
                  {stats.decreases}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Variance</span>
                <span className="text-sm font-medium">{stats.totalVarianceQty.toFixed(0)} units</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Impact */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Financial Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Variance Value</span>
                <span className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  £{stats.totalVarianceValue.toFixed(2)}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                This represents the absolute value of all variances
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stock Take Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Cycle Count Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Description</Label>
              <p className="font-medium">{setupData?.formData.description}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Date</Label>
              <p className="font-medium">
                {setupData?.formData.date ? format(setupData.formData.date, "PPP") : "N/A"}
              </p>
            </div>
            <div>
              <Label className="text-muted-foreground">Warehouse</Label>
              <p className="font-medium">{setupData?.formData.warehouse || "All Warehouses"}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Assigned To</Label>
              <p className="font-medium">{setupData?.formData.assignedTo}</p>
            </div>
            {setupData?.countType === "abc-based" && (
              <div>
                <Label className="text-muted-foreground">ABC Class</Label>
                <Badge 
                  variant="outline"
                  className={
                    setupData.formData.abcClass === "A" 
                      ? "border-red-500 text-red-700 dark:text-red-400"
                      : setupData.formData.abcClass === "B"
                      ? "border-yellow-500 text-yellow-700 dark:text-yellow-400"
                      : "border-green-500 text-green-700 dark:text-green-400"
                  }
                >
                  Class {setupData.formData.abcClass}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Counted Items Table */}
      <Card className="flex-1 overflow-hidden flex flex-col mb-6">
        <CardHeader>
          <CardTitle>Counted Items</CardTitle>
          <CardDescription>Review all counted items and variances</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px]">ABC Class</TableHead>
                <TableHead className="w-[100px]">Location</TableHead>
                <TableHead className="w-[100px] text-right">System Qty</TableHead>
                <TableHead className="w-[100px] text-right">Counted Qty</TableHead>
                <TableHead className="w-[100px] text-right">Variance</TableHead>
                <TableHead className="w-[120px] text-right">Value Impact</TableHead>
                <TableHead className="w-[150px]">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No items counted
                  </TableCell>
                </TableRow>
              ) : (
                countedItems.map((item) => {
                  const countedQty = parseFloat(item.countedQty) || 0;
                  const variance = countedQty - item.systemQty;
                  const hasVariance = variance !== 0;

                  return (
                    <TableRow key={item.code} className={hasVariance ? "bg-orange-50/50 dark:bg-orange-900/10" : ""}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={
                            item.abcClass === "A" 
                              ? "border-red-500 text-red-700 dark:text-red-400"
                              : item.abcClass === "B"
                              ? "border-yellow-500 text-yellow-700 dark:text-yellow-400"
                              : "border-green-500 text-green-700 dark:text-green-400"
                          }
                        >
                          Class {item.abcClass}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.location}</TableCell>
                      <TableCell className="text-right font-medium">
                        {item.systemQty} {item.unit}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {countedQty} {item.unit}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-medium ${getVarianceColor(item.systemQty, item.countedQty)}`}>
                          {getVarianceText(item.systemQty, item.countedQty)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-medium ${getVarianceColor(item.systemQty, item.countedQty)}`}>
                          {getVarianceValueText(item.systemQty, item.countedQty, item.value)}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.notes || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Final Notes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Final Notes</CardTitle>
          <CardDescription>Add any final comments or observations</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Enter any final notes or comments about this cycle count..."
            value={finalNotes}
            onChange={(e) => setFinalNotes(e.target.value)}
            rows={4}
          />
        </CardContent>
      </Card>

      </div>

      {/* Fixed Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigateTo("stock-takes/cycle-count/count", { setupData })}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Count
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExportReport} className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
            <Button 
              onClick={handleFinalize}
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700 gap-2"
            >
              {isSubmitting ? (
                <>
                  <Save className="h-4 w-4 animate-spin" />
                  Finalising...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Finalise Cycle Count
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
